<!-- =========================================================================================
  File Name: CompanyEdit.vue
  Description: Entity Edit Page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/company/pixinvent
========================================================================================== -->

<template>
  <div id="page-company-edit">
    <vs-alert class="mb-4" color="danger" title="Entity Not Found" :active.sync="company_not_found">
      <span>Entity record with id: {{ $route.params.id }} not found. </span>
      <span>
        <span>Check </span><router-link :to="{name:'page-company-list'}" class="text-inherit underline">All
        Entities</router-link>
      </span>
    </vs-alert>

    <div class="vs-row" v-if="data">
      <div class="vs-col w-full mb-base">
        <div class="vs-row">
          <div class="vs-col w-full">
            <company-general-details
              ref="general"
              v-model="general_data"
              :loading="loading"
            />
          </div>
          <div class="vs-col w-full">
            <company-profile
                ref="profile"
                v-model="profile_data"
                :has-lloyds="hasLloyds"
                :lloyds-syndicate="isLloydsSyndicate"
                :loading="loading"
            />
          </div>
          <div class="vs-col w-full">
            <company-branding
                ref="branding"
                v-model="branding_data"
                :current-logo="profile_data.CompanyLogo"
                :loading="loading"
                @delete-logo="profile_data.CompanyLogo = null"
            />
          </div>
          <div class="vs-col w-full">
            <company-external-data
                ref="external"
                v-model="external_data"
                :loading="loading"
            />
          </div>
        </div>
        <div class="vs-row">
          <div class="vs-col w-full">
            <company-edit-buttons
              :data="data"
              @save="saveChanges"
              @cancel="cancel"
              :loading="loading"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import CompanyGeneralDetails from "../forms/CompanyGeneralDetails";
import CompanyExternalData from "../forms/CompanyExternalData";
import CompanyProfile from "../forms/CompanyProfile";
import CompanyBranding from '../forms/CompanyBranding';
import CompanyEditButtons from "@/views/pages/companies/edit/CompanyEditButtons";
import CompanyValidation from "@/validation/Company";
import ViewModelMixin from '@/components/mixins/ViewModelMixin';
import SaveModelMixin from '@/components/mixins/SaveModelMixin';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import FormValidateMixin from '../../../../components/mixins/FormValidateMixin';
CompanyValidation.init();

export default {
  components: {
    CompanyEditButtons,
    CompanyGeneralDetails,
    CompanyProfile,
    CompanyExternalData,
    CompanyBranding,
  },
  mixins: [
    ViewModelMixin,
    SaveModelMixin,
    NotificationMixin,
    FormValidateMixin,
  ],
  data () {
    return {
      data: null,
      company_not_found: false,
      profile_data: {},
      general_data: {},
      external_data: {
        external_links: {},
        webservice_parameters: {},
        webServices: [],
      },
      branding_data: {},
      forms: [
        'general',
        'profile',
        'external',
        'branding',
      ]
    }
  },
  computed: {
    hasLloyds() {
      return !!(this.general_data.CompanyType_LloydsStatus_FK && this.general_data.CompanyType_LloydsStatus_FK.id)
    },
    isLloydsSyndicate() {
      let id = parseInt(this.general_data.CompanyType_LloydsStatus_FK);

      if (!id
        && this.general_data.CompanyType_LloydsStatus_FK
        && this.general_data.CompanyType_LloydsStatus_FK.id
      ) {
        id = parseInt(this.general_data.CompanyType_LloydsStatus_FK.id);
      }

      // ID for Lloyds Syndicate
      return (id === 17) ? true : false;
    }
  },
  methods: {
    getParams(validationOnly = false) {
      const companyData = this.setModelCopy({
        ...this.general_data,
      });

      companyData.profile = this.profile_data;
      companyData.external_links = this.external_data.external_links;
      companyData.webservice_parameters = this.external_data.webServices;

      const references = [
        'CompanyAncestor_FK',
        'CompanyType_Entity_FK',
        'CompanyGroup_FK',
        'CompanyType_LloydsStatus_FK',
        'ParentCompany_FK',
        'SecondaryParentCompany_FK',
        'ProducerStatusType_FK',
        'SalesCountryType_FK',
      ];

      const multiReferences = [
        {
          array: 'external_links',
          key: 'LinkType_FK',
        },
        {
          array: 'email_addresses',
          key: [
            'EmailType_FK',
          ],
        },
      ];

      this.setMultiReferences(multiReferences, companyData);
      this.setReferences(references, companyData);

      // Is RefData but endpoint requires a string
      if (companyData.profile.PartnershipStatus && companyData.profile.PartnershipStatus.id) {
        companyData.profile.PartnershipStatus = String(companyData.profile.PartnershipStatus.id);
      }
      
      if (companyData.profile.PartnershipSeniority && companyData.profile.PartnershipSeniority.id) {
        companyData.profile.PartnershipSeniority = String(companyData.profile.PartnershipSeniority.id);
      }

      if (companyData.profile.ClassificationDetail && companyData.profile.ClassificationDetail.id) {
        companyData.profile.ClassificationDetail = String(companyData.profile.ClassificationDetail.id);
      }

      if (companyData.profile.Contact_UnderwriterExecutive_FK && companyData.profile.Contact_UnderwriterExecutive_FK.id) {
        companyData.profile.Contact_UnderwriterExecutive_FK = String(companyData.profile.Contact_UnderwriterExecutive_FK.id);
      }

      if (companyData.profile.Contact_AreaManager_FK && companyData.profile.Contact_AreaManager_FK.id) {
        companyData.profile.Contact_AreaManager_FK = String(companyData.profile.Contact_AreaManager_FK.id);
      }

      if (companyData.profile.Contact_PlacingBroker_FK && companyData.profile.Contact_PlacingBroker_FK.id) {
        companyData.profile.Contact_PlacingBroker_FK = String(companyData.profile.Contact_PlacingBroker_FK.id);
      }

      return companyData;
    },
    getLogoParams() {
      const params = new FormData();
      params.set('_method', 'post');
      
      for (const field in this.branding_data) {
        params.set(field, this.branding_data[field]);
      }

      return params;
    },
    validate() {
      let valid = true;

      this.forms.forEach(form => {
        if (!this.$refs[form].validate()) {
          valid = false;
        }
      })

      return valid;
    },
    async saveChanges() {
      this.loading = true;

      if (!this.validate()) {
        this.notifyError('Not all required fields have been provided');
        this.loading = false;

        return false;
      }

      const companyData = this.getParams();

      try {
        await this.$api.companies.update(this.$route.params.id, companyData);

        if (this.branding_data.logoFile) {
          const logoData = this.getLogoParams();
          await this.$api.companies.updateLogo(this.$route.params.id, logoData);
        }

        this.$vs.notify({
          color: 'success',
          title: 'Entity updated',
          text: 'The entity was successfully updated',
          position: 'top-right',
        })
      } catch (e) {
        this.addErrors(e, true);

        if (e.response.status !== 422) {
          this.$vs.notify({
            color: 'danger',
            title: 'Error occurred',
            text: 'Not able to update entity. Please try again.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-right'
          })
        }

        if (e.response.status === 422) {
          let message = null;
          
          if (e.response.data.errors.CompanyCode) {
            message = e.response.data.errors.CompanyCode[0];
          }

          this.notifyValidationError(message);
        }

      } finally {
        this.$router.push({name: 'company.view', params:{id: this.$route.params.id}});
      }

      this.loading = false;
    },
    cancel() {
      this.$router.push({name: 'company.view', params:{id: this.$route.params.id}});
    },
    async fetch(companyId) {
      try {
        const { data } = await this.$api.companies.get(companyId);
        this.data = data.data;

        let emailAddresses = this.data.email_addresses;

        if (!data.data.email_addresses || data.data.email_addresses.length == 0) {
          emailAddresses = [{
            EmailAddress: null,
            EmailType_FK: null,
            IsPrimaryEmail: null
          }];
        }

        this.general_data = {
          id: this.data.id,
          CompanyName: this.data.CompanyName,
          CompanyCode: this.data.CompanyCode,
          CompanyGroup_FK: this.data.CompanyGroup_FK,
          CompanyType_Entity_FK: this.data.CompanyType_Entity_FK,
          CompanyLogo: this.data.CompanyLogo,
          CompanyAncestor_FK: this.data.CompanyAncestor_FK,
          ParentCompany_FK: this.data.ParentCompany_FK,
          SecondaryParentCompany_FK: this.data.SecondaryParentCompany_FK,
          CompanyExtension: this.data.CompanyExtension,
          CompanyType_LloydsStatus_FK: this.data.CompanyType_LloydsStatus_FK,
          SalesCountryType_FK: this.data.SalesCountryType_FK,
          ProducerStatusType_FK: this.data.ProducerStatusType_FK,
          email_addresses: emailAddresses,
          IsEnabled: this.data.IsEnabled,
          IsInsurer: this.data.IsInsurer,
          IsCoverholder: this.data.IsCoverholder,
          IsProducer: this.data.IsProducer,
          IsOther: this.data.IsOther,
          IsSubProducer: this.data.IsSubProducer,
        };

        this.profile_data = this.data.profile;
        this.profile_data.id = this.data.profile.CompanyProfile_PK;

        this.external_data = {
          id: this.data.id,
          external_links: this.data.external_links,
          webservice_parameters: this.data.webservice_parameters,
          webServices: [],
        }
      } catch (e) {
        console.error(e)
      } finally {
        if (!this.data) {
          this.company_not_found = true
        }
      }
    }
  },
}

</script>
